import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Service } from '../types';

export class ServiceService {
  // Add new service
  static async addService(
    salonId: string,
    serviceData: Omit<Service, 'id' | 'salonId' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'services'), {
        ...serviceData,
        salonId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      return docRef.id;
    } catch (error) {
      console.error('Error adding service:', error);
      throw new Error('Failed to add service');
    }
  }

  // Update service
  static async updateService(
    serviceId: string,
    updates: Partial<Omit<Service, 'id' | 'salonId' | 'createdAt'>>
  ): Promise<void> {
    try {
      const serviceRef = doc(db, 'services', serviceId);
      await updateDoc(serviceRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating service:', error);
      throw new Error('Failed to update service');
    }
  }

  // Get all services for a salon
  static async getServices(salonId: string): Promise<Service[]> {
    try {
      const q = query(
        collection(db, 'services'),
        where('salonId', '==', salonId)
      );

      const querySnapshot = await getDocs(q);
      const services: Service[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        services.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Service);
      });

      // Sort client-side instead of requiring composite index
      return services.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error getting services:', error);
      throw new Error('Failed to get services');
    }
  }

  // Get service by ID
  static async getService(serviceId: string): Promise<Service | null> {
    try {
      const docRef = doc(db, 'services', serviceId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Service;
      }
      return null;
    } catch (error) {
      console.error('Error getting service:', error);
      throw new Error('Failed to get service');
    }
  }

  // Delete service (soft delete by setting isActive to false)
  static async deleteService(serviceId: string): Promise<void> {
    try {
      await this.updateService(serviceId, { isActive: false });
    } catch (error) {
      console.error('Error deleting service:', error);
      throw new Error('Failed to delete service');
    }
  }
}
